// Tournaments → Cup puzzles (contracts/tournaments.md §2; GET /admin/v1/cups/pools and /cups/pools/{week}/{pool}):
// every entrant plays their own set of 10 from their bracket's pool of the cup's ISO week, seeded with the weekly
// sets. Pick a week (this week by default; the picker marks the seeded ones) and a bracket to see its pool's
// calibration (target difficulty, sets), how many players were dealt a set and how many sets are in use, then its
// sets 50 a page: difficulty mean ± spread, the 10 types, and how many players hold each (any cup of the week).
// Filter to the sets dealt to players; sort by set or by players (within the page: the API pages by set). A set
// opens its puzzles with answers and who got it (/tournaments/puzzles/:setId). The view lives in the URL.

import { Link, useNavigate, useSearchParams } from "react-router";
import { CUP_POOL_PAGE, type CupPool, type CupPools, type CupPoolSet, useCupPools, useCupPoolSets } from "../../api/cups";
import { tableStyles } from "../../components/table";
import { Button, Card, Chip, EmptyState, ErrorState, Loading, PageHeader, Segmented, Select, Stat } from "../../components/ui";
import { KIND_LABEL, poolName, STATUS_TONE } from "../../lib/cups";
import { formatDateTime, isoWeekOf, isoWeekSpan, todayUtc } from "../../lib/dates";
import { fmtInt, fmtPct } from "../../lib/format";
import m from "../messaging/messaging.module.css";
import styles from "../pages.module.css";
import { Spread, TypeChips } from "./components";
import c from "./cups.module.css";

type Sort = "k" | "players";

export function CupPuzzlesPage() {
  const [params, setParams] = useSearchParams();
  const asked = params.get("week") ?? undefined;
  const pools = useCupPools(asked);
  const data = pools.data;
  const week = data?.week ?? null;
  const pool = data?.pools.find((p) => p.pool === params.get("pool")) ?? data?.pools[0] ?? null;
  const used = params.get("used") === "true";
  const page = Math.max(1, Math.floor(Number(params.get("page"))) || 1);
  const sort: Sort = params.get("sort") === "players" ? "players" : "k";
  const update = (next: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(next)) {
          if (v === null) p.delete(k);
          else p.set(k, v);
        }
        return p;
      },
      { replace: true },
    );

  return (
    <>
      <PageHeader
        title="Cup puzzles"
        meta={<span>Every entrant plays their own set of 10 from their bracket&rsquo;s pool for the cup&rsquo;s week, so answers can&rsquo;t be shared. Answers stay on the server.</span>}
      />
      {pools.isPending ? (
        <Loading rows={8} />
      ) : pools.isError ? (
        <ErrorState error={pools.error} onRetry={() => void pools.refetch()} />
      ) : !data || !week ? (
        <Card>
          <EmptyState title="No cup pools are seeded">
            <span className="mono">pnpm seed:sets</span> (backend/api) seeds each week&rsquo;s weekly sets and its cup pools from content/out; cups can&rsquo;t deal sets
            until their week is seeded.
          </EmptyState>
        </Card>
      ) : (
        <>
          <WeekBar data={data} week={week} onWeek={(w) => update({ week: w, page: null })} />
          {data.pools.length === 0 ? (
            <div className={m.notice} data-tone="warning" style={{ marginBottom: 16 }} data-testid="week-unseeded">
              <strong>{week} isn&rsquo;t seeded.</strong>
              <span>Cups opening this week can&rsquo;t deal sets until its pools are seeded (pnpm seed:sets in backend/api).</span>
            </div>
          ) : (
            <>
              <div className={c.filters}>
                <Segmented
                  label="Bracket"
                  value={pool?.pool ?? ""}
                  onChange={(v) => update({ pool: v, page: null })}
                  options={data.pools.map((p) => ({ value: p.pool, label: poolName(p.pool), title: p.pool }))}
                />
              </div>
              {pool ? (
                <>
                  <PoolStats pool={pool} />
                  <PoolSets
                    week={week}
                    pool={pool}
                    used={used}
                    page={page}
                    sort={sort}
                    onUsed={(v) => update({ used: v ? "true" : null, page: null })}
                    onPage={(p) => update({ page: p > 1 ? String(p) : null })}
                    onSort={(s) => update({ sort: s === "k" ? null : s })}
                  />
                </>
              ) : null}
            </>
          )}
        </>
      )}
    </>
  );
}

/** The week picker (seeded weeks, this week and the one shown), what is seeded, and the week's cups. */
function WeekBar({ data, week, onWeek }: { data: CupPools; week: string; onWeek: (week: string) => void }) {
  const thisWeek = isoWeekOf(todayUtc());
  const seeded = new Map(data.weeks.map((w) => [w.week, w]));
  const options = [...new Set([...seeded.keys(), thisWeek, week])].sort();
  const shown = seeded.get(week);
  const first = data.weeks[0]?.week;
  const last = data.weeks.at(-1)?.week;
  return (
    <div className={c.weekBar}>
      <div className={c.weekPick}>
        <Select value={week} onChange={(e) => onWeek(e.target.value)} aria-label="Week" data-testid="week-picker" style={{ width: 330 }}>
          {options.map((w) => (
            <option key={w} value={w}>
              {w} · {isoWeekSpan(w)}
              {w === thisWeek ? " · this week" : ""}
              {seeded.has(w) ? "" : " · not seeded"}
            </option>
          ))}
        </Select>
        <span className={c.dim} data-testid="seeded-weeks">
          {data.weeks.length === 0
            ? "No week is seeded."
            : `${data.weeks.length} ${data.weeks.length === 1 ? "week" : "weeks"} seeded${first !== last ? `, ${first} to ${last}` : `: ${first}`}`}
          {shown ? ` · ${week} seeded ${formatDateTime(shown.seededAt)} by ${shown.generator}` : ""}
        </span>
      </div>
      <div className={c.weekCups} data-testid="week-cups">
        <span className="monoLabel">Cups this week</span>
        {data.cups.length === 0 ? (
          <span className={c.dim}>none</span>
        ) : (
          data.cups.map((cup) => (
            <span key={cup.id} className={c.weekCup}>
              <Link to={`/tournaments/${cup.id}`} title={`${KIND_LABEL[cup.kind]} · opens ${formatDateTime(cup.opensAt)}`}>
                {cup.title || KIND_LABEL[cup.kind]}
              </Link>
              <Chip tone={STATUS_TONE[cup.status]}>{cup.status}</Chip>
            </span>
          ))
        )}
      </div>
    </div>
  );
}

function PoolStats({ pool }: { pool: CupPool }) {
  return (
    <div className={styles.statStrip} data-testid="pool-stats">
      <Stat label="Sets" value={fmtInt(pool.count)} sub={`k ${pool.first}–${pool.first + pool.count - 1}`} />
      <Stat
        label="Target difficulty"
        value={pool.targetMean === null ? "—" : `${pool.targetMean.toFixed(2)}${pool.targetSpread === null ? "" : ` ± ${pool.targetSpread.toFixed(2)}`}`}
        sub="mean ± spread of a set"
      />
      <Stat label="Players dealt" value={fmtInt(pool.players)} sub="one set a player and cup" />
      <Stat
        label="Sets in use"
        value={fmtInt(pool.setsInUse)}
        sub={`${fmtPct(pool.count ? pool.setsInUse / pool.count : 0)} of the pool${pool.setsInUse && pool.players > pool.setsInUse ? ` · ${(pool.players / pool.setsInUse).toFixed(1)} players a set` : ""}`}
      />
    </div>
  );
}

function PoolSets({
  week,
  pool,
  used,
  page,
  sort,
  onUsed,
  onPage,
  onSort,
}: {
  week: string;
  pool: CupPool;
  used: boolean;
  page: number;
  sort: Sort;
  onUsed: (used: boolean) => void;
  onPage: (page: number) => void;
  onSort: (sort: Sort) => void;
}) {
  const navigate = useNavigate();
  const q = useCupPoolSets(week, pool.pool, { page, used, sort });
  const total = q.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / CUP_POOL_PAGE));
  // The API sorts across the whole pool (?sort=players); keep its order.
  const sets = q.data?.sets ?? [];
  const open = (s: CupPoolSet) => navigate(`/tournaments/puzzles/${encodeURIComponent(s.setId)}`);

  return (
    <Card
      title={`${poolName(pool.pool)} sets`}
      padded={false}
      actions={
        <div className={c.filters} style={{ margin: 0 }}>
          <Segmented
            label="Show"
            value={used ? "used" : "all"}
            onChange={(v) => onUsed(v === "used")}
            options={[
              { value: "all", label: `All · ${fmtInt(pool.count)}` },
              { value: "used", label: `Dealt · ${fmtInt(pool.setsInUse)}`, title: "Sets dealt to at least one player this week" },
            ]}
          />
          <Select value={sort} onChange={(e) => onSort(e.target.value as Sort)} aria-label="Sort" data-testid="sets-sort" style={{ width: 170 }}>
            <option value="k">By set number</option>
            <option value="players">Most players first</option>
          </Select>
        </div>
      }
    >
      {q.isPending ? (
        <Loading rows={8} />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : sets.length === 0 ? (
        <EmptyState title={used ? "No set dealt yet" : "No sets"}>{used ? "Nobody has been dealt a set from this pool this week." : "This pool has no sets."}</EmptyState>
      ) : (
        <>
          <div className={tableStyles.wrap} style={{ border: 0, borderRadius: 0 }} aria-busy={q.isPlaceholderData || undefined}>
            <table className={tableStyles.table}>
              <thead>
                <tr>
                  <th>Set</th>
                  <th>Difficulty</th>
                  <th>Puzzles</th>
                  <th data-align="right">Players</th>
                </tr>
              </thead>
              <tbody>
                {sets.map((s) => (
                  <tr key={s.setId} data-clickable data-testid="set-row" data-set={s.setId} onClick={(e) => !(e.target as HTMLElement).closest("a, button") && open(s)}>
                    <td>
                      <Link to={`/tournaments/puzzles/${encodeURIComponent(s.setId)}`} className={c.setLink} title={s.setId}>
                        {s.k}
                      </Link>
                    </td>
                    <td>
                      <Spread mean={s.mean} spread={s.spread} target={pool.targetMean} />
                    </td>
                    <td>
                      <TypeChips types={s.types} />
                    </td>
                    <td data-align="right" className={tableStyles.mono} data-testid="set-players">
                      {s.players ? fmtInt(s.players) : <span className={c.dim}>0</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={tableStyles.footer} style={{ padding: "12px 16px" }}>
            <span className="mono" data-testid="sets-page">
              Page {page} of {pages} · {fmtInt(total)} {used ? "dealt " : ""}sets

            </span>
            <div className={tableStyles.pager}>
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="sets-prev">
                ← Previous
              </Button>
              <Button size="sm" variant="ghost" disabled={page >= pages || q.isPlaceholderData} onClick={() => onPage(page + 1)} data-testid="sets-next">
                Next →
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
